import React, { useState, useRef, useEffect } from 'react';
import { useCurrency } from '../context/CurrencyContext';

const GlobeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/><path d="M2 12h20"/></svg>
);

const POPULAR_CURRENCIES = ['GBP', 'USD', 'EUR', 'JPY', 'AUD', 'CAD', 'INR', 'CHF'];

const CurrencySelector = ({ className = '' }) => {
  const { currency, setCurrency, getAvailableCurrencies, currencyInfo, loading } = useCurrency();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  const availableCurrencies = getAvailableCurrencies();
  
  const sortedCurrencies = [...availableCurrencies].sort((a, b) => {
    const aPopular = POPULAR_CURRENCIES.includes(a.code);
    const bPopular = POPULAR_CURRENCIES.includes(b.code);
    
    if (aPopular && !bPopular) return -1;
    if (!aPopular && bPopular) return 1;
    if (aPopular && bPopular) {
      return POPULAR_CURRENCIES.indexOf(a.code) - POPULAR_CURRENCIES.indexOf(b.code);
    }
    return a.code.localeCompare(b.code);
  });
  
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const handleSelect = (code) => {
    setCurrency(code);
    setIsOpen(false);
  };
  
  if (loading) {
    return (
      <div className={`flex items-center gap-1 text-gray-400 ${className}`}>
        <GlobeIcon />
        <span className="text-sm">...</span>
      </div>
    );
  }
  
  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg hover:bg-gray-100 transition-colors text-gray-700"
        title="Change currency"
      >
        <GlobeIcon />
        <span className="text-sm font-medium">{currency}</span>
        <span className="text-sm text-gray-500">{currencyInfo.symbol}</span>
      </button>
      
      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-gray-200 z-50 max-h-80 overflow-y-auto">
          <div className="p-2">
            <div className="text-xs font-semibold text-gray-500 px-2 py-1 mb-1">
              SELECT CURRENCY
            </div>
            
            <div className="mb-2">
              <div className="text-xs text-gray-400 px-2 py-1">Popular</div>
              {sortedCurrencies
                .filter(c => POPULAR_CURRENCIES.includes(c.code))
                .map(({ code, symbol, name }) => (
                  <button
                    key={code}
                    onClick={() => handleSelect(code)}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-left transition-colors ${
                      currency === code
                        ? 'bg-terracotta/10 text-terracotta'
                        : 'hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-medium w-12">{code}</span>
                      <span className="text-sm text-gray-500">{name}</span>
                    </div>
                    <span className="text-gray-400">{symbol}</span>
                  </button>
                ))}
            </div>
            
            <div className="border-t border-gray-100 my-2" />
            
            <div>
              <div className="text-xs text-gray-400 px-2 py-1">Other currencies</div>
              {sortedCurrencies
                .filter(c => !POPULAR_CURRENCIES.includes(c.code))
                .map(({ code, symbol, name }) => (
                  <button
                    key={code}
                    onClick={() => handleSelect(code)}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-left transition-colors ${
                      currency === code
                        ? 'bg-terracotta/10 text-terracotta'
                        : 'hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-medium w-12">{code}</span>
                      <span className="text-sm text-gray-500 truncate">{name}</span>
                    </div>
                    <span className="text-gray-400">{symbol}</span>
                  </button>
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CurrencySelector;
